import { computed, unref, ref } from 'vue'
import type { AuthV1, CustomAuthError } from '@zoomcatalog/auth'
import { makeAuthServiceOptions } from '@/components/CanvasContainer/useSetupServices'
import useAxios from '@/composable/RequestUtils/useAxios'
import {
	SSO_POST_TOKEN_EXCHANGE,
	AUTH_POST_REFRESH_TOKEN,
} from '@/config/endpoints'
import { useSelector } from '@/store/rootStore'

export const useRefreshToken = () => {
	const endpoints = useSelector((state) => state.sniffer.endpoints)
	const { hasData, response: axiosResponse, errors, post } = useAxios()
	const data = computed(() => (hasData.value ? unref(axiosResponse).data : {}))

	const response = ref<AuthV1 | CustomAuthError>()
	const userToken = computed(() => {
		const resp = unref(response) as any
		return resp?.v2_tokens?.user_token || resp?.access_token || ''
	})
	const refreshToken = computed(() => {
		const resp = unref(response) as any
		return resp?.v2_tokens?.refresh_token || resp?.refresh_token || ''
	})

	const requestToken = async (refreshToken: string) => {
		const authServiceOptions = makeAuthServiceOptions()
		try {
			response.value = await authServiceOptions
				.refreshToken(refreshToken)
				.catch((error: any) => {
					throw error
				})

			// unable to use authHandlers.handleRefreshToken
			// handler not defined in this context
			return Promise.resolve(response)
		} catch (error) {
			errors.value = error as any
			return Promise.reject(unref(errors.value))
		}
	}

	const userTokenOld = computed(() =>
		hasData.value ? unref(data).user_token : null
	)
	const refreshTokenOld = computed(() =>
		hasData.value ? unref(data).refresh_token : null
	)

	const requestTokenOld = async (payload: Record<string, unknown>) => {
		const { endpoint } = AUTH_POST_REFRESH_TOKEN
		try {
			await post(endpoint, payload, {
				baseURL: endpoints.value.AUTH_API_ENDPOINT,
			}).catch((error) => {
				error.customMessage = 'Unable to refresh the token'
				throw error
			})
			return Promise.resolve(data)
		} catch (error) {
			errors.value = error as any
			return Promise.reject(unref(errors.value))
		}
	}

	return {
		userToken,
		refreshToken,
		requestToken,
		userTokenOld,
		refreshTokenOld,
		requestTokenOld,
	}
}

export const useTokenExchange = () => {
	const endpoints = useSelector((state) => state.sniffer.endpoints)
	const { hasData, response, errors, post } = useAxios()
	const data = computed(() => (hasData.value ? unref(response).data : {}))
	const userToken = computed(() =>
		hasData.value ? unref(data).user_token : null
	)
	const refreshToken = computed(() =>
		hasData.value ? unref(data).refresh_token : null
	)

	const requestTokenExchange = async (payload: Record<string, unknown>) => {
		try {
			await post(SSO_POST_TOKEN_EXCHANGE.endpoint, payload, {
				baseURL: endpoints.value.SSO_API_ENDPOINT,
			}).catch((error) => {
				error.customMessage = 'Unable to exchange the token'
				throw error
			})
			return Promise.resolve(data)
		} catch (error) {
			errors.value = error as any
			return Promise.reject(unref(errors.value))
		}
	}
	return {
		userToken,
		refreshToken,
		requestTokenExchange,
	}
}

export default {}
